import { Routes, UrlSegment } from '@angular/router';

import { DashboardContainer } from './dashboard/dashboard-container/dashboard.container';
import { AuthLayoutComponent } from './layout/layouts/auth-layout/auth-layout.component';
import { BlankLayoutComponent } from './layout/layouts/blank-layout/blank-layout.component';
import { MainContainerComponent } from './layout/layouts/main-layout/main-layout.component';
import { AuthErrorComponent } from './shared/Components/auth-error/auth-error.component';
import { ServerErrorComponent } from './shared/Components/server-error/server-error.component';
import { ErrorResolverService } from './shared/Services/error-resolver/error-resolver.service';
import { emailTokenGuard } from './shared/Services/guards/email-token.guard';
import { loginGuard } from './shared/Services/guards/login.guard';
import {
  registrationStepsCanDeactivateGuard,
  registrationStepsGuard,
} from './shared/Services/guards/registration-steps.guard';
import { unauthenticatedGuard } from './shared/Services/guards/unauthenticated.guard';
import { userResolver } from './shared/Services/resolvers/user.resolver';

function stepsPath(url: UrlSegment[]) {
  if (
    url.length === 2 &&
    url[0].path === 'registration' &&
    new RegExp(`\\d+`).test(url[1].path)
  ) {
    return {
      consumed: url,
      posParams: {
        step: url[1],
      },
    };
  }
  return null;
}

export const routes: Routes = [
  {
    path: '',
    component: MainContainerComponent,
    resolve: [userResolver],
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
        data: { title: 'dashboard' },
      },
      {
        path: 'dashboard',
        component: DashboardContainer,
        canActivate: [loginGuard],
        data: { title: 'dashboard' },
      },
      {
        path: 'menus',
        loadChildren: () =>
          import('./menus/menus.routes').then((m) => m.routes),
      },
      {
        path: 'types',
        loadChildren: () =>
          import('./consumertypes/types.routes').then((m) => m.routes),
      },
      {
        path: 'diets',
        loadChildren: () =>
          import('./diets/diets.routes').then((m) => m.routes),
      },
      {
        path: 'dishes',
        loadChildren: () =>
          import('./dishes/dishes.routes').then((m) => m.routes),
      },
      {
        path: 'ingredients',
        loadChildren: () =>
          import('./ingredients/ingredients.routes').then((m) => m.routes),
      },
      {
        path: 'integrations',
        loadChildren: () =>
          import('./integrations/integrations.routes').then((m) => m.routes),
      },
      {
        path: 'locations',
        loadChildren: () =>
          import('./locations/locations.routes').then((m) => m.routes),
      },
      {
        path: 'inventory',
        loadChildren: () =>
          import('./procurement/procurement.routes').then((m) => m.routes),
      },
      {
        path: 'recipes',
        loadChildren: () =>
          import('./recipes/recipes.routes').then((m) => m.routes),
      },
      {
        path: 'rules',
        loadChildren: () =>
          import('./rules/rules.routes').then((m) => m.routes),
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./settings/settings.routes').then((m) => m.routes),
      },
      {
        path: 'templates',
        loadChildren: () =>
          import('./templates/templates.routes').then((m) => m.routes),
      },
      {
        path: 'declarations',
        loadChildren: () =>
          import('./declarations/declarations.routes').then(
            (m) => m.declarationsRoutes,
          ),
      },
      {
        path: 'spellcheck',
        loadChildren: () =>
          import('./spellcheck-dictionary/spellcheck-dictionary.routes').then(
            (m) => m.spellcheckDictionaryRoutes,
          ),
      },
      {
        path: 'import-replacements',
        loadChildren: () =>
          import('./import-replacements/import-replacements.routes').then(
            (m) => m.importReplacementsRoutes,
          ),
      },
    ],
  },
  {
    path: '',
    component: AuthLayoutComponent,
    canActivate: [],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./auth/Containers/login/login.container').then(
            (m) => m.LoginContainer,
          ),
        canActivate: [unauthenticatedGuard],
        data: { title: 'login' },
      },
      {
        path: 'reset/:uid/:token',
        loadComponent: () =>
          import('./auth/Containers/reset-password/reset-password.container').then(
            (m) => m.ResetPasswordContainer,
          ),
        canActivate: [unauthenticatedGuard],
        data: { title: 'reset-password' },
      },
      {
        path: 'reset',
        loadComponent: () =>
          import('./auth/Containers/reset-password/reset-password.container').then(
            (m) => m.ResetPasswordContainer,
          ),
        canActivate: [unauthenticatedGuard],
        data: { title: 'reset-password' },
      },
      {
        path: 'confirm-email/:key',
        loadComponent: () =>
          import('./auth/Containers/confirmation/confirmation.container').then(
            (m) => m.ConfirmationContainer,
          ),
        data: { title: 'confirm-email' },
      },
      {
        path: 'email_preferences/:email/:token',
        loadComponent: () =>
          import('./auth/Containers/email-preferences/email-preferences.container').then(
            (m) => m.EmailPreferencesContainer,
          ),
        canActivate: [emailTokenGuard],
        data: { title: 'email-pref' },
      },
      {
        path: 'accept-invitation/:key',
        loadComponent: () =>
          import('./auth/Containers/accept-invitation/accept-invitation.container').then(
            (m) => m.AcceptInvitationContainer,
          ),
        data: { title: 'accept-inv' },
      },
    ],
  },
  {
    path: '',
    component: BlankLayoutComponent,
    canActivate: [],
    children: [
      {
        matcher: stepsPath,
        loadComponent: () =>
          import('./auth/Containers/registration-steps/registration-steps.container').then(
            (m) => m.RegistrationStepsContainer,
          ),
        resolve: { user: userResolver },
        canActivate: [registrationStepsGuard],
        canDeactivate: [registrationStepsCanDeactivateGuard],
        data: { title: 'registration-steps' },
      },
      {
        path: 'registration/:affiliate',
        loadComponent: () =>
          import('./auth/Containers/registration/registration.container').then(
            (m) => m.RegistrationContainer,
          ),
        canActivate: [unauthenticatedGuard],
        data: { title: 'registration' },
      },
      {
        path: 'registration/reactivate/:uid/:token',
        loadComponent: () =>
          import('./auth/Containers/registration/registration.container').then(
            (m) => m.RegistrationContainer,
          ),
        data: { title: `registration`, reactivate: true },
      },
      {
        path: 'registration/restore/:uid/:token',
        loadComponent: () =>
          import('./auth/Containers/registration/registration.container').then(
            (m) => m.RegistrationContainer,
          ),
        data: { title: `registration`, restore: true },
      },
      {
        path: 'registration',
        loadComponent: () =>
          import('./auth/Containers/registration/registration.container').then(
            (m) => m.RegistrationContainer,
          ),
        canActivate: [unauthenticatedGuard],
        data: { title: 'registration' },
      },
      {
        path: '404',
        component: AuthErrorComponent,
        data: { title: '404' },
        canActivate: [ErrorResolverService],
      },
      {
        path: 'server-error',
        component: ServerErrorComponent,
      },
    ],
  },
  {
    path: '**',
    component: AuthErrorComponent,
    canActivate: [ErrorResolverService],
    data: { title: 'error' },
  },
];
